import { isEqual } from "@common/helpers/comparison";
import type { Column, ColumnPin } from "@common/types/views/views";
import { DEFAULT_VARIANT_KEY } from "@helpers/viewColumnVariant";
import type { DataGridLeafColumnInfo } from "../columns/columnLayout";
import type { DataGridColumnState } from "../types";
import {
    getValueIds,
    type SavedColumnOptions,
    VIEW_PIN,
} from "./savedViewColumns";

/** `customVariants` with `key` set to `value`, or removed when `value` is unset. */
const withVariant = ({
    customVariants,
    key,
    value,
}: {
    customVariants: Record<string, string> | undefined;
    key: string;
    value: string | undefined;
}) => {
    const { [key]: _previous, ...others } = customVariants ?? {};
    const next = value === undefined ? others : { ...others, [key]: value };
    return Object.keys(next).length > 0 ? next : undefined;
};

/** `state.order` completed the way TanStack reads it: missing ids go last. */
const getEffectiveOrder = (
    state: DataGridColumnState,
    leaves: readonly DataGridLeafColumnInfo[],
) => {
    const leafIds = leaves.map((leaf) => leaf.id);
    const leafIdSet = new Set(leafIds);
    const ordered = state.order.filter((id) => leafIdSet.has(id));
    const orderedIds = new Set(ordered);
    return [...ordered, ...leafIds.filter((id) => !orderedIds.has(id))];
};

/** A view column with its pin, width, and variant; unchanged entries keep their shape. */
const toViewColumn = ({
    id,
    existing,
    pin,
    size,
    variant,
}: {
    id: string;
    existing: Column | undefined;
    pin: ColumnPin | undefined;
    size: number | undefined;
    /** Columns with `variants` only. */
    variant: { key: string; value: string | undefined } | undefined;
}): Column => {
    const savedOptions: SavedColumnOptions = existing?.options ?? {};
    const {
        pin: _pin,
        size: _size,
        customVariants: savedVariants,
        variant: savedLegacyVariant,
        ...rest
    } = savedOptions;
    const customVariants = variant
        ? withVariant({ customVariants: savedVariants, ...variant })
        : savedVariants;
    const legacyVariant =
        variant?.key === DEFAULT_VARIANT_KEY
            ? variant.value
            : savedLegacyVariant;
    const options = {
        ...rest,
        ...(customVariants !== undefined && { customVariants }),
        ...(legacyVariant !== undefined && { variant: legacyVariant }),
        ...(pin !== undefined && { pin }),
        ...(size !== undefined && { size }),
    };
    const hasOptions =
        existing?.options !== undefined || Object.keys(options).length > 0;
    const column: Column = hasOptions ? { id, options } : { id };
    return existing && isEqual(existing, column) ? existing : column;
};

/**
 * Table column state as a saved view's shown columns, in display order. A
 * variant also mirrors into the legacy `options.variant`, which ViewTable
 * still reads. Options it doesn't own, and columns this table doesn't
 * render, are kept in place.
 */
export const columnStateToViewColumns = ({
    state,
    viewColumns,
    leaves,
}: {
    state: DataGridColumnState;
    viewColumns: readonly Column[];
    leaves: readonly DataGridLeafColumnInfo[];
}): Column[] => {
    const valueIds = new Set(getValueIds(leaves));
    const startPinnedIds = state.pinning.start.filter((id) => valueIds.has(id));
    const endPinnedIds = state.pinning.end.filter((id) => valueIds.has(id));
    const startIds = new Set(startPinnedIds);
    const endIds = new Set(endPinnedIds);
    const unpinnedIds = getEffectiveOrder(state, leaves).filter(
        (id) => valueIds.has(id) && !startIds.has(id) && !endIds.has(id),
    );
    const existingById = new Map(
        viewColumns.map((column) => [column.id, column]),
    );
    const variantKeyById = new Map(
        leaves.map((leaf) => [leaf.id, leaf.variants?.key]),
    );
    const variantOf = (id: string) => {
        const key = variantKeyById.get(id);
        return key === undefined
            ? undefined
            : { key, value: state.variants[id] };
    };
    const pinOf = (id: string): ColumnPin | undefined => {
        if (startIds.has(id)) {
            return VIEW_PIN.start;
        }
        return endIds.has(id) ? VIEW_PIN.end : undefined;
    };
    const columns = [...startPinnedIds, ...unpinnedIds, ...endPinnedIds]
        .filter((id) => !(id in state.visibility) || state.visibility[id])
        .map((id) =>
            toViewColumn({
                id,
                existing: existingById.get(id),
                pin: pinOf(id),
                size: state.sizing[id],
                variant: variantOf(id),
            }),
        );
    viewColumns.forEach((column, index) => {
        if (!valueIds.has(column.id)) {
            columns.splice(Math.min(index, columns.length), 0, column);
        }
    });
    return columns;
};
